<script lang="ts">
import { Dialog, DialogScrollContent } from '@gat-ship/vue-components/dialog'
import { type GatButtonProps } from '@gat-ship/vue-components'
import { testIds } from '@gat-ship/types'
import { globalUtils } from '@gat-ship/utils'

export interface ConfirmDialogMessage {
  title?: string
  textStyle?: 'base' | 'soft'
  message?: ConfirmDialogMessageType
}

export type ConfirmDialogMessageType = string | ((...args: any[]) => string)

export interface ConfirmDialogProps {
  title?: string
  message?: ConfirmDialogMessageType
  messages?: ConfirmDialogMessage[]
  type?: 'success' | 'error' | 'warning' | 'info'
  buttons?: {
    ok?: Omit<GatButtonProps, 'onClick'>
    cancel?: Omit<GatButtonProps, 'onClick'>
  }
}
</script>

<script setup lang="ts">
defineOptions({
  inheritAttrs: false
})

const props = defineProps<ConfirmDialogProps>()

const emit = defineEmits<{
  (e: 'update:open', openState: boolean): void
  (e: 'click:ok'): void
  (e: 'click:cancel'): void
}>()

const isOpen = defineModel<boolean>('open', { default: false })

function onOkClick() {
  emit('click:ok')
}

function onCancelClick() {
  isOpen.value = false
  emit('click:cancel')
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogScrollContent :overlay-z-index="100">
      <div>
        <slot name="header" :class="'flex w-full items-center gap-2'">
          <div class="flex w-full items-center gap-2">
            <div v-if="type === 'error'" :class="[
              'flex h-10 w-10 items-center justify-center rounded-md'
              /* { 'bg-red-100': type === 'error' } */
            ]">
              <gat-icon :icon="$icons.common.error" :stroke-width="1.5" class="text-destructive" />
            </div>
            <div class="flex flex-col" :class="message ? 'gap-1.5' : ''">
              <slot name="title" :class="'text-lg font-semibold leading-none'">
                <div class="text-lg font-semibold leading-none">
                  {{ title }}
                </div>
              </slot>
            </div>
          </div>
        </slot>
      </div>
      <slot :class="'text-sm leading-none'">
        <div v-if="!messages" class="text-sm leading-none whitespace-pre-line">
          {{ typeof message === 'function' ? message() : message }}
        </div>
        <template v-else>
          <template v-for="message in messages" :key="globalUtils.functions.generateShortUniqueId()">
            <div v-if="message.title" class="text-base font-semibold leading-none">
              {{ message.title }}
            </div>
            <div class="text-sm leading-tight whitespace-pre-line"
              :class="message.textStyle === 'soft' ? 'text-soft' : ''">
              {{ message.message }}
            </div>
          </template>
        </template>
      </slot>
      <slot name="footer" :class="'flex flex-row-reverse gap-2 px-4 pt-4'">
        <div class="flex flex-row-reverse gap-2 px-4 pt-4">
          <gat-button v-bind="buttons?.ok" :label="buttons?.ok?.label ?? 'Confirm'"
            :variant="buttons?.ok?.variant ?? 'outline'"
            :test-id="buttons?.ok?.testId ?? testIds.common.dialog.saveButton" @click="onOkClick()" />
          <gat-button v-bind="buttons?.cancel" :label="buttons?.cancel?.label ?? 'Cancel'"
            :variant="buttons?.cancel?.variant ?? 'ghost'" @click="onCancelClick()" />
        </div>
      </slot>
    </DialogScrollContent>
  </Dialog>
</template>
